import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import img from "./all_bg.jpg";

const BACKEND_URL = "https://api.bbsfacultyprofile.com";
const TABLE_NAME = "awards";

const Awards = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [formData, setFormData] = useState({
    year: null,
    award_type: "",
    award_name: "",
    institution: "",
    country: "",
  });
  const [recordId, setRecordId] = useState(null);

  const fetchRecords = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/${TABLE_NAME}`, {
        method: "GET",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch records");
    const data = await res.json();

const processed = data
  .map((rec) => ({
    ...rec,
    year: rec.year ? new Date(rec.year) : null,
  }))
  .sort((a, b) => {
    if (!a.year) return 1;
    if (!b.year) return -1;
    return b.year.getTime() - a.year.getTime(); // Sort by year and month (latest first)
  });

setRecords(processed);
    } catch (err) {
      toast.error(err.message);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      year: formData.year ? format(formData.year, "yyyy-MM") + "-01" : null,
    };
    try {
      const url = recordId
        ? `${BACKEND_URL}/api/${TABLE_NAME}/${recordId}`
        : `${BACKEND_URL}/api/${TABLE_NAME}`;
      const method = recordId ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to save record");
      toast.success(
        recordId ? "Record updated successfully!" : "Award added successfully!"
      );
      setFormData({
        year: null,
        award_type: "",
        award_name: "",
        institution: "",
        country: "",
      });
      setRecordId(null);
      fetchRecords();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleEdit = (rec) => {
    setFormData({
      year: rec.year ? new Date(rec.year) : null,
      award_type: rec.award_type || "",
      award_name: rec.award_name || "",
      institution: rec.institution || "",
      country: rec.country || "",
    });
    setRecordId(rec.id);
    toast.info("Editing record...");
  };

  const handleDelete = (rec) => {
    const toastId = toast.info(
      <div>
        Delete this award?
        <button
          onClick={async () => {
            try {
              const res = await fetch(
                `${BACKEND_URL}/api/${TABLE_NAME}/${rec.id}`,
                {
                  method: "DELETE",
                  credentials: "include",
                }
              );
              if (!res.ok) throw new Error("Failed to delete record");
              fetchRecords();
              toast.update(toastId, {
                render: "Record deleted successfully!",
                type: "success",
                autoClose: 3000,
              });
            } catch (err) {
              toast.update(toastId, {
                render: `Delete error: ${err.message}`,
                type: "error",
                autoClose: 3000,
              });
            }
          }}
          style={{
            marginLeft: "10px",
            padding: "3px 8px",
            background: "#ef4444",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Confirm
        </button>
      </div>,
      { autoClose: false, closeOnClick: false }
    );
  };

  const inputStyle = {
    padding: "10px",
    marginTop: "5px",
    border: "1px solid #ccc",
    borderRadius: "6px",
    width: "100%",
    fontSize: "16px",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "40px 20px",
        fontFamily: "Arial, sans-serif",
        backgroundImage: `url(${img})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <ToastContainer position="top-center" autoClose={3000} />
      <div
        style={{
          maxWidth: "800px",
          margin: "0 auto",
          background: "#fff",
          padding: "30px 40px",
          borderRadius: "10px",
          boxShadow: "0px 0px 12px rgba(0,0,0,0.1)",
        }}
      >
        <h2
          style={{
            textAlign: "center",
            color: "#1e3a8a",
            marginBottom: "25px",
          }}
        >
          Awards
        </h2>

        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column" }}
        >
          {/* Year */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Awarding Year (mm/yyyy){" "}
              <span
                style={{
                  fontWeight: 300,
                  fontSize: "0.85em",
                  marginLeft: "6px",
                  color: "#666",
                }}
              >
                (start with latest award)
              </span>
            </label>
            <DatePicker
              selected={formData.year}
              onChange={(date) => setFormData({ ...formData, year: date })}
              dateFormat="MM/yyyy"
              showMonthYearPicker
              placeholderText="Select month/year"
              style={inputStyle}
            />
          </div>

          {/* Award Type */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Award Type
            </label>
            <select
              name="award_type"
              value={formData.award_type}
              onChange={handleChange}
              required
              style={inputStyle}
            >
              <option value="">Select Type</option>
              <option value="National">National</option>
              <option value="International">International</option>
            </select>
          </div>

          {/* Award Name */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Award Name
            </label>
            <input
              type="text"
              name="award_name"
              value={formData.award_name}
              onChange={handleChange}
              required
              style={inputStyle}
            />
          </div>

          {/* Institution */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Awarding Institution
            </label>
            <input
              type="text"
              name="institution"
              value={formData.institution}
              onChange={handleChange}
              required
              style={inputStyle}
            />
          </div>

          {/* Country */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Country
            </label>
            <input
              type="text"
              name="country"
              value={formData.country}
              onChange={handleChange}
              required
              style={inputStyle}
            />
          </div>

          <button
            type="submit"
            style={{
              background: "#3b82f6",
              color: "white",
              padding: "12px",
              border: "none",
              borderRadius: "6px",
              marginTop: "20px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            {recordId ? "Update Record" : "Add Award"}
          </button>
        </form>

        {/* Table */}
        {records.length > 0 && (
          <div style={{ marginTop: "30px", overflowX: "auto" }}>
            <h3 style={{ color: "#1e3a8a" }}>Your Awards</h3>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginTop: "10px",
              }}
            >
              <thead>
                <tr>
                  {[
                    "Year",
                    "Type",
                    "Award Name",
                    "Institution",
                    "Country",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        background: "#f1f5f9",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((rec) => (
                  <tr key={rec.id}>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.year
                        ? `${String(rec.year.getMonth() + 1).padStart(
                            2,
                            "0"
                          )}/${rec.year.getFullYear()}`
                        : ""}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.award_type}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.award_name}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.institution}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.country}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        textAlign: "center",
                      }}
                    >
                      <button
                        onClick={() => handleEdit(rec)}
                        style={{
                          background: "#2563eb",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          padding: "5px 10px",
                          marginRight: "5px",
                          cursor: "pointer",
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(rec)}
                        style={{
                          background: "#ef4444",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          padding: "5px 10px",
                          cursor: "pointer",
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: "25px" }}>
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              background: "#1e3a8a",
              color: "white",
              padding: "10px 20px",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default Awards;
